import { TriangleAlert } from 'lucide-react';
import { useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';

import { FormSection } from '@nucleus-commerce/admin-kit/forms';
import { Alert, AlertDescription, AlertTitle } from '@nucleus-commerce/ui/components/alert';
import { Form } from '@nucleus-commerce/ui/components/form';

import { fromBehaviour, toBehaviourPatch } from '../api/mappers';
import { useChatSettingsForm, type ChatSettingsTabProps } from '../hooks/use-chat-settings-form';
import { behaviourSchema, type BehaviourForm } from '../schemas/behaviour-schema';

import { FormAlert, SaveButton, SwitchField, TextField } from './form-controls';

const GROUPS = ['behaviour', 'emails', 'retention', 'sla', 'attribution', 'help'] as const;

const TOGGLES = [
  'showTypingToStaff',
  'soundEnabled',
  'allowUploads',
  'allowRating',
  'allowTranscript',
  'suggestedRepliesEnabled',
] as const;

/** Widget features, help centre cards, timers, emails, SLA targets, attribution and retention. */
export function BehaviourTab({ storeId, settings, readOnly }: ChatSettingsTabProps) {
  const { t } = useTranslation('chatSettings');
  const { form, submit, saving } = useChatSettingsForm<BehaviourForm>({
    storeId,
    schema: behaviourSchema,
    defaultValues: fromBehaviour(settings),
    toPatch: toBehaviourPatch,
    groups: GROUPS,
    readOnly,
  });
  const autoTranscript = useWatch({ control: form.control, name: 'emails.autoTranscript' });
  const purge = useWatch({ control: form.control, name: 'retention.purge' });
  const months = useWatch({ control: form.control, name: 'retention.purgeClosedAfterMonths' });

  return (
    <Form {...form}>
      <form onSubmit={submit} noValidate className="flex max-w-3xl flex-col gap-6">
        <FormAlert message={form.formState.errors.root?.server?.message} />
        <FormSection
          title={t('behaviour.widgetTitle')}
          description={t('behaviour.widgetDescription')}
        >
          {TOGGLES.map((name) => (
            <SwitchField
              key={name}
              control={form.control}
              name={`behaviour.${name}`}
              label={t(`fields.${name}`)}
              description={t(`fields.${name}Hint`)}
              disabled={readOnly}
            />
          ))}
        </FormSection>
        <FormSection title={t('behaviour.helpTitle')} description={t('behaviour.helpDescription')}>
          {(['widgetSearch', 'suggestArticles'] as const).map((name) => (
            <SwitchField
              key={name}
              control={form.control}
              name={`help.${name}`}
              label={t(`fields.${name}`)}
              description={t(`fields.${name}Hint`)}
              disabled={readOnly}
            />
          ))}
        </FormSection>
        <FormSection
          title={t('behaviour.timersTitle')}
          description={t('behaviour.timersDescription')}
        >
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              name="behaviour.idleCloseMinutes"
              label={t('fields.idleCloseMinutes')}
              description={t('fields.idleCloseMinutesHint')}
              suffix={t('units.minutes')}
              numeric
              disabled={readOnly}
            />
            <TextField
              control={form.control}
              name="behaviour.missedAfterSeconds"
              label={t('fields.missedAfterSeconds')}
              description={t('fields.missedAfterSecondsHint')}
              suffix={t('units.seconds')}
              numeric
              disabled={readOnly}
            />
            <TextField
              control={form.control}
              name="behaviour.autoCloseResolvedHours"
              label={t('fields.autoCloseResolvedHours')}
              description={t('fields.autoCloseResolvedHoursHint')}
              suffix={t('units.hours')}
              numeric
              disabled={readOnly}
            />
          </div>
        </FormSection>
        <FormSection title={t('behaviour.salesTitle')}>
          <TextField
            control={form.control}
            name="behaviour.discountCapPerAgentPerDay"
            label={t('fields.discountCapPerAgentPerDay')}
            description={t('fields.discountCapPerAgentPerDayHint')}
            suffix={t('units.perDay')}
            numeric
            disabled={readOnly}
          />
          <TextField
            control={form.control}
            name="behaviour.triggerDiscountCapPerDay"
            label={t('fields.triggerDiscountCapPerDay')}
            description={t('fields.triggerDiscountCapPerDayHint')}
            suffix={t('units.perDay')}
            numeric
            disabled={readOnly}
          />
          <SwitchField
            control={form.control}
            name="behaviour.freeSizerOffer"
            label={t('fields.freeSizerOffer')}
            description={t('fields.freeSizerOfferHint')}
            disabled={readOnly}
          />
          <TextField
            control={form.control}
            name="attribution.windowDays"
            label={t('fields.attributionWindowDays')}
            description={t('fields.attributionWindowDaysHint')}
            suffix={t('units.days')}
            numeric
            disabled={readOnly}
          />
        </FormSection>
        <FormSection title={t('behaviour.slaTitle')} description={t('behaviour.slaDescription')}>
          <div className="grid gap-4 sm:grid-cols-2">
            <TextField
              control={form.control}
              name="sla.firstResponseSeconds"
              label={t('fields.firstResponseSeconds')}
              description={t('fields.firstResponseSecondsHint')}
              suffix={t('units.seconds')}
              placeholder={t('fields.noTarget')}
              numeric
              disabled={readOnly}
            />
            <TextField
              control={form.control}
              name="sla.resolutionHours"
              label={t('fields.resolutionHours')}
              description={t('fields.resolutionHoursHint')}
              suffix={t('units.hours')}
              placeholder={t('fields.noTarget')}
              numeric
              disabled={readOnly}
            />
          </div>
        </FormSection>
        <FormSection
          title={t('behaviour.emailsTitle')}
          description={t('behaviour.emailsDescription')}
        >
          <SwitchField
            control={form.control}
            name="emails.replyWhenAway"
            label={t('fields.replyWhenAway')}
            description={t('fields.replyWhenAwayHint')}
            disabled={readOnly}
          />
          <SwitchField
            control={form.control}
            name="emails.offlineAck"
            label={t('fields.offlineAck')}
            description={t('fields.offlineAckHint')}
            disabled={readOnly}
          />
          <SwitchField
            control={form.control}
            name="emails.autoTranscript"
            label={t('fields.autoTranscript')}
            description={t('fields.autoTranscriptHint')}
            disabled={readOnly}
          />
          {autoTranscript ? (
            <TextField
              control={form.control}
              name="emails.autoTranscriptAfterMinutes"
              label={t('fields.autoTranscriptAfterMinutes')}
              suffix={t('units.minutes')}
              numeric
              disabled={readOnly}
            />
          ) : null}
          <TextField
            control={form.control}
            name="emails.continueUrlTemplate"
            label={t('fields.continueUrlTemplate')}
            description={t('fields.continueUrlTemplateHint')}
            placeholder="https://shop.example.com/chat/c/{token}"
            mono
            disabled={readOnly}
          />
        </FormSection>
        <FormSection title={t('behaviour.retentionTitle')}>
          <SwitchField
            control={form.control}
            name="retention.purge"
            label={t('fields.purgeClosed')}
            description={t('fields.purgeClosedHint')}
            disabled={readOnly}
          />
          {purge ? (
            <>
              <TextField
                control={form.control}
                name="retention.purgeClosedAfterMonths"
                label={t('fields.purgeClosedAfterMonths')}
                suffix={t('units.months')}
                numeric
                disabled={readOnly}
              />
              <Alert variant="destructive">
                <TriangleAlert aria-hidden />
                <AlertTitle>{t('behaviour.retentionWarningTitle')}</AlertTitle>
                <AlertDescription>
                  {t('behaviour.retentionWarning', { months: months || '…' })}
                </AlertDescription>
              </Alert>
            </>
          ) : null}
        </FormSection>
        <SaveButton saving={saving} dirty={form.formState.isDirty} hidden={readOnly} />
      </form>
    </Form>
  );
}
